import { createFileRoute, Link } from '@tanstack/react-router'
import { ArrowRight } from 'lucide-react'
import { Button } from '@/components/ui/button'
import { itemList, jsonLdScript } from '@/lib/schema'

export const Route = createFileRoute('/_site/http/')({
  component: HttpStatusIndexPage,
  head: () => ({
    meta: [
      { title: 'HTTP Status Codes — What They Mean & Fixes' },
      { name: 'description', content: 'Complete reference for HTTP status codes. Learn what each code means, common causes, and how to fix them. Covers 2xx, 3xx, 4xx, and 5xx responses.' },
      { property: 'og:title', content: 'HTTP Status Codes Reference | Uptrack' },
      { property: 'og:description', content: 'Complete reference for HTTP status codes. Learn what each code means, common causes, and how to fix them.' },
      { property: 'og:image', content: 'https://uptrack.app/og/http.png' },
      { name: 'twitter:card', content: 'summary_large_image' },
    ],
    links: [
      { rel: 'canonical', href: 'https://uptrack.app/http' },
    ],
    scripts: [
      jsonLdScript({
        '@context': 'https://schema.org',
        '@type': 'WebPage',
        name: 'HTTP Status Codes Reference',
        description: 'Complete reference for HTTP status codes with explanations, causes, and fixes.',
        url: 'https://uptrack.app/http',
      }),
      jsonLdScript(itemList(
        'HTTP Status Codes',
        categories.flatMap((cat) =>
          cat.codes.map((c) => ({
            name: `HTTP ${c.code} ${c.name}`,
            url: `https://uptrack.app/http/${c.code}`,
            description: c.desc,
          })),
        ),
        'https://uptrack.app/http',
      )),
    ],
  }),
})

const categories = [
  {
    title: '2xx Success',
    color: 'text-status-up',
    codes: [
      { code: 200, name: 'OK', desc: 'Request succeeded' },
      { code: 201, name: 'Created', desc: 'Resource created' },
      { code: 204, name: 'No Content', desc: 'Success, no body' },
    ],
  },
  {
    title: '3xx Redirection',
    color: 'text-primary',
    codes: [
      { code: 301, name: 'Moved Permanently', desc: 'Permanent redirect' },
      { code: 302, name: 'Found', desc: 'Temporary redirect' },
      { code: 304, name: 'Not Modified', desc: 'Cached version is current' },
    ],
  },
  {
    title: '4xx Client Errors',
    color: 'text-status-degraded',
    codes: [
      { code: 400, name: 'Bad Request', desc: 'Malformed request' },
      { code: 401, name: 'Unauthorized', desc: 'Authentication required' },
      { code: 403, name: 'Forbidden', desc: 'Access denied' },
      { code: 404, name: 'Not Found', desc: "Resource doesn't exist" },
      { code: 405, name: 'Method Not Allowed', desc: 'Wrong HTTP method' },
      { code: 408, name: 'Request Timeout', desc: 'Server timed out waiting' },
      { code: 409, name: 'Conflict', desc: 'Conflicts with current state' },
      { code: 410, name: 'Gone', desc: 'Permanently removed' },
      { code: 413, name: 'Payload Too Large', desc: 'Request body too big' },
      { code: 415, name: 'Unsupported Media Type', desc: 'Wrong content type' },
      { code: 422, name: 'Unprocessable Entity', desc: 'Validation failed' },
      { code: 429, name: 'Too Many Requests', desc: 'Rate limited' },
      { code: 451, name: 'Unavailable For Legal Reasons', desc: 'Censored' },
    ],
  },
  {
    title: '5xx Server Errors',
    color: 'text-status-down',
    codes: [
      { code: 500, name: 'Internal Server Error', desc: 'Generic server failure' },
      { code: 502, name: 'Bad Gateway', desc: 'Invalid upstream response' },
      { code: 503, name: 'Service Unavailable', desc: 'Overloaded or in maintenance' },
      { code: 504, name: 'Gateway Timeout', desc: "Upstream didn't respond in time" },
      { code: 520, name: 'Web Server Returns Unknown Error', desc: 'Cloudflare-specific' },
      { code: 522, name: 'Connection Timed Out', desc: "Cloudflare can't reach origin" },
    ],
  },
]

function HttpStatusIndexPage() {
  return (
    <>
      <section>
        <div className="container mx-auto px-4 py-12 md:py-16">
          <h1 className="text-[2.25rem] font-semibold leading-[1.1] tracking-tight md:text-[3rem]">HTTP Status Codes Reference</h1>
          <p className="mt-5 max-w-2xl text-lead text-body">
            What each HTTP status code means, why it happens, and how to fix it. Written for developers who need
            answers, not spec jargon.
          </p>
        </div>
      </section>

      {categories.map((cat) => (
        <section key={cat.title} className="border-t border-border">
          <div className="container mx-auto px-4 py-12 md:py-[72px]">
            <h2 className={`text-[1.75rem] font-semibold leading-tight tracking-tight ${cat.color}`}>{cat.title}</h2>
            <div className="mt-8 grid grid-cols-1 gap-4 sm:grid-cols-2 lg:grid-cols-3">
              {cat.codes.map((c) => (
                <a
                  key={c.code}
                  href={`/http/${c.code}`}
                  className="group rounded-lg border border-border bg-card p-5 transition-colors hover:border-primary"
                >
                  <div className="flex items-baseline gap-3">
                    <span className={`num text-2xl font-semibold ${cat.color}`}>{c.code}</span>
                    <span className="font-medium text-foreground">{c.name}</span>
                  </div>
                  <p className="mt-2 text-[15px] leading-relaxed text-body">{c.desc}</p>
                </a>
              ))}
            </div>
          </div>
        </section>
      ))}

      <section className="border-y border-border bg-card">
        <div className="container mx-auto grid grid-cols-1 items-center gap-6 px-4 py-12 md:py-[72px] lg:grid-cols-12">
          <div className="lg:col-span-8">
            <h2 className="text-[1.75rem] font-semibold leading-tight tracking-tight md:text-headline">Catch HTTP errors before your users do</h2>
            <p className="mt-3 max-w-xl text-lead text-body">Every plan includes 30-second checks and alert confirmation.</p>
          </div>
          <div className="lg:col-span-4 lg:text-right">
            <Button size="lg" className="h-11 px-5 text-[15px]" asChild>
              <Link to="/pricing">
                See plans
                <ArrowRight className="ml-1 h-4 w-4" aria-hidden="true" />
              </Link>
            </Button>
            <p className="mt-3 text-sm text-muted-foreground">from $3/mo · 30-day money-back guarantee</p>
          </div>
        </div>
      </section>
    </>
  )
}
